import React from 'react';
import { formatCurrency } from '../../../../../utils/formatUtils';
import FormInfo from '../../../../components/messages/FormInfo';
import {
  APTransactionEntryGLItem,
  APTransactionEntryJobItem,
  UnitOfMeasure,
} from '../../../../Interfaces/Accounting/ACForms.interfaces';
import { useQuery } from '@tanstack/react-query';
import { useCompanyContext } from '@/src/app/context/CompanyContext';
import {
  fecthJobs,
  fetchJCJobPhases,
  fetchPhaseCostTypes,
} from '@/src/app/services/accounting/JobsContractsService';
import { fecthGLAccounts } from '@/src/app/services/accounting/GLAccountService';
import {
  JCJobSummary,
  JCPhase,
} from '@/src/app/Interfaces/Accounting/JobsContracts.interface';
import { fetchUnitsOfMeasure } from '@/src/app/services/accounting/ACFormsService';

type APItemSummaryProps = {
  item: APTransactionEntryJobItem | APTransactionEntryGLItem;
  index: number;
};

const APItemSummary = ({ item, index }: APItemSummaryProps) => {
  const { selectedCompany } = useCompanyContext();
  const { description, total } = item;
  const isJobCost = 'job' in item && !!item.job;
  const isGLCost = 'glAcct' in item && !!item.glAcct;
  const [job, phase] = isJobCost ? [item.job, item.phase] : ['', 0];

  const jobs = useQuery({
    queryKey: ['getAPEntryJobs', selectedCompany],
    queryFn: ({ signal }) =>
      fecthJobs({ companyID: selectedCompany!.id, status: [1] }, signal),
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const glItems = useQuery({
    queryKey: ['getAPEntryItems', selectedCompany],
    queryFn: ({ signal }) => {
      return fecthGLAccounts(selectedCompany!.id, [6, 7, 8, 9], signal);
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const phases = useQuery({
    queryKey: ['getJCJobPhases', selectedCompany, job],
    queryFn: ({ signal }) => {
      return fetchJCJobPhases(
        {
          companyID: selectedCompany!.id,
          job: job,
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const costTypes = useQuery({
    queryKey: ['getPhaseCostTypes', selectedCompany, job, phase],
    queryFn: ({ signal }) => {
      return fetchPhaseCostTypes(
        selectedCompany!.id,
        job,
        phase as string,
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const umRequest = useQuery({
    queryKey: ['getUnitOfMeasure'],
    queryFn: ({ signal }) => {
      return fetchUnitsOfMeasure(signal);
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const buildJobFields = (item: APTransactionEntryJobItem) => {
    const jobData = jobs.data?.find(
      (jobData: JCJobSummary) => jobData.Job === item.job
    );
    const costTypeData = costTypes.data?.find(
      (costType: { costType: number; description: string }) =>
        costType.costType === item.costType
    );

    const phaseData: JCPhase = phases.data?.find(
      (phase: JCPhase) => phase.Phase === item.phase
    );

    const umData: JCPhase = umRequest.data?.find(
      (um: UnitOfMeasure) => um.UM === item.um
    );

    return (
      <React.Fragment>
        <div className="col-12 sm:col-6" />
        <div className="col-12 sm:col-6">
          <FormInfo label="Job #" content={item.job} />
        </div>
        <div className="col-12 sm:col-6">
          <FormInfo label="Job Name" content={jobData.Description} />
        </div>
        <div className="col-12 sm:col-6">
          <FormInfo label="Cost Type" content={costTypeData.description} />
        </div>
        <div className="col-12 sm:col-6">
          <FormInfo
            label="Phase Code"
            content={`${phaseData.Phase} - ${phaseData.Description}`}
          />
        </div>
        <div className="col-12 sm:col-6">
          <FormInfo label="UoM" content={umData.Description} />
        </div>
        <div className="col-12 sm:col-6">
          <FormInfo label="Units" content={item.units ?? 0} />
        </div>
        <div className="col-12 sm:col-6">
          <FormInfo label="Price" content={formatCurrency(item.price ?? 0)} />
        </div>
      </React.Fragment>
    );
  };

  const buildGLFields = (item: APTransactionEntryGLItem) => {
    const glData = glItems.data?.find(
      (glAcct) => glAcct.GLAcct === item.glAcct
    );

    return (
      <div className="col-12 sm:col-6">
        <FormInfo
          label="GL Account"
          content={`${glData?.GLAcct} ${glData?.Description}`}
        />
      </div>
    );
  };

  return (
    <div className="grid mx-0 mb-4 mt-1">
      <div className="col-12">
        <FormInfo label={`Item ${index + 1}`} content={''} />
      </div>
      <div className="col-12 grid px-3">
        <div className="col-12 sm:col-6">
          <FormInfo
            label="Entry Type"
            content={isJobCost ? 'Job Cost' : 'GL'}
          />
        </div>
        {isGLCost && buildGLFields(item)}
        {isJobCost && buildJobFields(item)}
        <div className="col-12 sm:col-6">
          <FormInfo label="Description" content={description} />
        </div>
        <div className="col-12 sm:col-6">
          <FormInfo label="Total" content={formatCurrency(total ?? 0)} />
        </div>
      </div>
    </div>
  );
};

export default APItemSummary;
